import { useContext, useMemo, useState } from "react";
import { HouseContext } from "../../../../context/HouseContext";
import { Link } from "react-router-dom";
import { FaPencilAlt, FaTrashAlt } from "react-icons/fa";
import { deleteProperty } from "../../../../application/deleteProperty";
import Table from "../../../../components/Table/Table";
import { FaSistrix, FaRegListAlt } from "react-icons/fa";
import ConfirmDialog from "../../../../components/ConfirmDialog/ConfirmDialog";
import { FaClipboardList } from "react-icons/fa6";

function PropertyList() {
  const { houses } = useContext(HouseContext);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [propertyIdToDelete, setPropertyIdToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleConfirm = async () => {
    try {
      setIsDeleting(true);
      await deleteProperty(propertyIdToDelete);
      setIsConfirmOpen(false);
      window.location.reload();
    } catch (error) {
      console.error("Error al eliminar la propiedad:", error);
      // Mostrar un mensaje de error al usuario
      alert("Error al eliminar la propiedad. Por favor, inténtalo de nuevo.");
    } finally {
      setIsDeleting(false);
    }
  };

  const handleCancel = () => {
    setIsConfirmOpen(false);
    setPropertyIdToDelete(null);
  };

  // Estado para controlar la vista (lista o tarjetas)
  const [viewMode, setViewMode] = useState("list"); // "list" o "cards"

  // Estado para la búsqueda
  const [searchTerm, setSearchTerm] = useState("");

  // Filtrar casas por el término de búsqueda
  const filteredHouses = houses.filter((house) =>
    house.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const columns = useMemo(
    () => [
      {
        Header: "Imagen",
        accessor: "images[0]",
        Cell: ({ value }) => (
          <img src={value} alt={houses.name} className="w-20 h-16 rounded-lg" />
        ),
      },
      {
        Header: "Nombre",
        accessor: "name",
        Cell: ({ value }) => <span className="capitalize">{value}</span>,
      },
      {
        Header: "Localidad",
        accessor: "country",
        Cell: ({ value }) => <span className="capitalize">{value}</span>,
      },
      {
        Header: "Dirección",
        accessor: "address",
        Cell: ({ value }) => <span className="capitalize">{value}</span>,
      },
      {
        Header: "Tipo",
        accessor: "type",
        Cell: ({ value }) => <span className="capitalize">{value}</span>,
      },
      {
        Header: "Estado",
        accessor: "state",
        Cell: ({ value }) => <span className="capitalize">{value}</span>,
      },
      {
        Header: "Destacado",
        accessor: "highlights",
        Cell: ({ value }) => (
          <span className="capitalize">{value ? "Si" : "No"}</span>
        ),
      },
      {
        Header: "Editar",
        accessor: "id",
        Cell: ({ value }) => (
          <Link to={`/dashboard/propiedades/editar/${value}`}>
            <FaPencilAlt className="text-lg text-gray-700 dark:text-gray-200 w-full" />
          </Link>
        ),
      },
      {
        Header: "Eliminar",
        accessor: "idFirebase",
        Cell: ({ value }) => (
          <>
            <button
              onClick={() => {
                setPropertyIdToDelete(value);
                setIsConfirmOpen(true);
              }}
              className="text-red-500 hover:text-red-700"
              disabled={isDeleting}
            >
              <FaTrashAlt className="text-lg w-full" />
            </button>
          </>
        ),
      },
    ],
    [houses, isDeleting]
  );

  return (
    <div>
      <ConfirmDialog
        isOpen={isConfirmOpen}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
        message="¿Estás seguro de que quieres eliminar esta propiedad?"
        isDeleting={isDeleting}
      />
      {/* Botones para alternar entre la vista en lista y en tarjetas */}
      <div className="flex space-x-4 mb-4 justify-end mx-4">
        <button
          onClick={() => setViewMode("list")}
          className={`px-4 py-2 ${
            viewMode === "list" ? "bg-blue-500 text-white" : "bg-gray-200"
          }`}
        >
          <FaRegListAlt className="text-2xl" />
        </button>
        <button
          onClick={() => setViewMode("cards")}
          className={`px-4 py-2 ${
            viewMode === "cards" ? "bg-blue-500 text-white" : "bg-gray-200"
          }`}
        >
          <FaClipboardList className="text-2xl" />
        </button>
      </div>

      {/* Mostrar según el modo de vista */}
      {viewMode === "list" ? (
        // Vista en lista utilizando el componente Table
        <Table
          columns={columns}
          data={filteredHouses}
          title={"Lista de propiedades"}
        />
      ) : (
        // Vista en tarjetas
        <div className="lg:px-4 sm:px-2 px-4 mx-auto sm:w-11/12">
          <div className="w-full text-center">
            <h1 className="font-bold text-4xl text-black pb-6">
              Lista de propiedades
            </h1>
          </div>
          <div className="flex flex-row mb-1 sm:mb-0 justify-between w-full">
            <p>Número de Registros: {houses.length}</p>
            <div className="text-end">
              <div className="flex flex-col md:flex-row w-3/4 md:w-full max-w-sm md:space-x-3 space-y-3 md:space-y-0 justify-center">
                <div className="relative">
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Buscar propiedad..."
                    className="px-4 py-2 border border-gray-300 rounded-lg w-full"
                  />

                  <FaSistrix className="absolute right-0 top-0 mt-2 mr-4 text-2xl" />
                </div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 pt-4">
            {filteredHouses.map((house) => (
              <div key={house.id} className="bg-white p-4 rounded-lg shadow-lg">
                <img
                  src={house.images[0]}
                  alt={house.name}
                  className="w-full h-48 object-cover rounded-lg"
                />
                <h3 className="text-xl font-semibold mt-4">{house.name}</h3>
                <p className="text-gray-500">{house.country}</p>
                <p className="text-gray-500">{house.address}</p>
                <p className="text-gray-500">{house.type}</p>
                <p className="text-gray-500">{house.state}</p>
                <div className="text-end">
                  <Link
                    to={`/dashboard/propiedades/editar/${house.id}`}
                    className="text-blue-500 mt-2 inline-block"
                  >
                    <FaPencilAlt className="inline mr-1" /> Editar Propiedad
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default PropertyList;
